import React, { useEffect, useRef, useState } from 'react'
import WhiteBoxContainer from '../../../../components/WhiteBoxContainer'
import styles from './gallery.module.scss'
import { CatalogoProductInfo } from '../CatalogoProductInfo'
import Lupa from './assets/lupa.svg'
import Size from '../../../../../common/utils/media-queries-setup'
import ThumbnailCarousel from './ThumbnailCarousel'
import { Pagination } from './Pagination'
import { MainImage } from './MainImage'
import { CloseButton } from './CloseButton'
import { getGalleryImages } from './utils'

interface Props {
  producto: CatalogoProductInfo
  onCloseModal?: () => void
  onClick?: () => void
}

const MINIMUM_IMAGES = 1

const defaultProductImage =
  'https://media.rodi.es/marca/default/neumaticos/recortado/imagen-neumatico.png'

export default function Gallery({ producto, onCloseModal }: Props) {
  const isMobile = Size.useMedia({
    mobile: true,
    tablet: false,
    desktop: false,
  })

  const images = getGalleryImages(
    producto.imagenes,
    'ficha',
    defaultProductImage,
    MINIMUM_IMAGES
  )

  const MarcaLogo = require(`../../../../../common/assets/images/logo_marcas/${producto.marca.logo}.svg`)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [startIndex, setStartIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleThumbnails, setVisibleThumbnails] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const calculateVisibleThumbnails = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const thumbnailWidth = 90
        setVisibleThumbnails(Math.floor(containerWidth / thumbnailWidth))
      }
    }

    calculateVisibleThumbnails()
    window.addEventListener('resize', calculateVisibleThumbnails)

    return () => {
      window.removeEventListener('resize', calculateVisibleThumbnails)
    }
  }, [])

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index)

    if (
      index >= startIndex + visibleThumbnails - 2 &&
      startIndex + visibleThumbnails < images.length
    ) {
      setStartIndex((prev) =>
        Math.min(prev + 1, images.length - visibleThumbnails)
      )
    }

    if (index <= startIndex && startIndex > 0) {
      setStartIndex((prev) => Math.max(prev - 1, 0))
    }
  }

  const openModal = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setShowModal(false)
    }, 400) // retardamos para dar tiempo a la animacion de irse el modal y que react no renderize el modal por ultima vez con el estado de showing
  }
  return (
    <div className={styles.gallery}>
      <WhiteBoxContainer>
        <div className={styles.gallery__main}>
          <div className={styles.gallery__main_header}>
            <div className={styles.gallery__brand_logo}>
              <MarcaLogo className={styles.marcaLogo} />
            </div>
            <div className={styles.gallery__estaciones_logo}>
              {producto.estaciones?.map((estacion, index) => {
                const Icon = estacion.icon
                return Icon ? (
                  <Icon
                    key={index}
                    className={styles.estacionIcon}
                    alt={estacion.name}
                  />
                ) : null
              })}
            </div>
          </div>
          {/* Main Image Display */}
          <MainImage
            image={images[currentIndex]}
            alt={`Product Image ${producto.modelo}`}
            handleNext={images.length > 1 ? handleNext : null}
            handlePrevious={handlePrevious}
            isModal={showModal}
            onClick={openModal}
          />
          <div className={styles.gallery__main_footer}>
            {/* Pagination */}
            <Pagination currentIndex={currentIndex} total={images.length} />
            {/* Zoom Icon */}
            <div className={styles.gallery__zoom_icon}>
              <Lupa onClick={openModal} />
            </div>
          </div>
        </div>
      </WhiteBoxContainer>
      {/* Thumbnails */}
      {!isMobile && (
        <ThumbnailCarousel
          images={images}
          currentIndex={currentIndex}
          onThumbnailClick={handleThumbnailClick}
          isMobile={isMobile}
        />
      )}
      {/* Zoom Modal */}
      {showModal && (
        <div className={styles.gallery__modal}>
          <div
            className={styles.gallery__modal_overlay}
            onClick={handleCloseModal}></div>
          <div
            className={`${styles.gallery__modal_content} ${
              closing ? styles.closing : ''
            }`}
            onClick={(e) => e.stopPropagation()}>
            <div className={styles.gallery__modal_header}>
              <div className={styles.gallery__modal_close}>
                <CloseButton onClick={handleCloseModal} />
              </div>
            </div>
            <div className={styles.gallery__modal_image_content}>
              <MainImage
                image={images[currentIndex]}
                alt={`Product Image ${producto.modelo}`}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
                isModal={showModal}
              />
              <ThumbnailCarousel
                images={images}
                currentIndex={currentIndex}
                onThumbnailClick={handleThumbnailClick}
                isMobile={isMobile}
              />
            </div>
            <div className={styles.gallery__modal_footer}>
              <Pagination currentIndex={currentIndex} total={images.length} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
